/**
 * SubscriptionTiersPanel — iter164 · Single-Tier („Premium") Refactor
 * ────────────────────────────────────────────────────────────────────
 * Nur noch EINE Abo-Stufe „Premium" (Wunsch iter164). Der wählbare monatliche
 * Preis liegt zwischen 4,99 € und 49,99 € (Bounds aus Config-Service).
 *
 * Backend-Kompatibilität: die alten drei Felder (standard/premium/vip) werden
 * beim Save auf denselben Premium-Preis gesetzt, damit die Backend-Validierung
 * nicht bricht. Für die Fan-Seite ist ausschließlich das Premium-Feld relevant.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Loader2, Save, Euro, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";

function parseEuro(str) {
  if (str === "" || str == null) return NaN;
  return Number(String(str).replace(",", "."));
}

export default function SubscriptionTiersPanel() {
  const { t } = useTranslation();
  const [price, setPrice] = useState("");
  const [bounds, setBounds] = useState({ min_price_eur: 4.99, max_price_eur: 49.99 });
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/studio/subscription-tiers");
      if (data.bounds) setBounds(data.bounds);
      setTiers(data.tiers);
      if (data.tiers) {
        // Bevorzuge premium_price_eur, sonst standard, sonst leer.
        const p = data.tiers.premium_price_eur ?? data.tiers.standard_price_eur ?? "";
        setPrice(String(p));
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Konnte Abo-Konfiguration nicht laden");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validation = useMemo(() => {
    const p = parseEuro(price);
    const errs = [];
    if (!Number.isFinite(p)) return { ok: false, errs: ["Bitte Preis eingeben"] };
    if (p < bounds.min_price_eur) errs.push(`Preis darf ${bounds.min_price_eur.toFixed(2)} € nicht unterschreiten`);
    if (p > bounds.max_price_eur) errs.push(`Preis darf ${bounds.max_price_eur.toFixed(2)} € nicht überschreiten`);
    return { ok: errs.length === 0, errs, price: p };
  }, [price, bounds]);

  const save = async () => {
    setError(""); setSuccess("");
    if (!validation.ok) { setError(validation.errs[0]); return; }
    setSaving(true);
    try {
      // iter165 · Nur premium_price_eur senden — Backend füllt Legacy-Felder auto.
      const { data } = await api.put("/studio/subscription-tiers", {
        premium_price_eur: validation.price,
      });
      setTiers(data.tiers);
      setSuccess("Abo-Preis gespeichert.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    setError(""); setSuccess("");
    try {
      const { data } = await api.post("/studio/subscription-tiers/publish", {
        published: !(tiers?.published),
      });
      setTiers(data.tiers);
      setSuccess(data.tiers?.published ? "Premium-Abo veröffentlicht." : "Premium-Abo zurückgezogen.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Publish-Toggle fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="p-6 border border-[#E5E5E5] bg-white flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-[#999]" />
        <span className="text-sm text-[#666]">Lade Abo-Konfiguration…</span>
      </div>
    );
  }

  return (
    <section data-testid="sub-tiers-panel" className="space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-brand-black">Creator-Abo · Premium</h3>
          <p data-testid="sub-tiers-bounds-hint" className="text-xs text-[#666] mt-0.5">
            Zulässiger monatlicher Preis: {bounds.min_price_eur.toFixed(2)} € bis {bounds.max_price_eur.toFixed(2)} €.
            Vorteile aus der Monetarisierung sind für alle Premium-Fans identisch.
          </p>
        </div>
        {tiers && (
          <button
            data-testid="sub-tiers-publish-toggle"
            onClick={togglePublish}
            className={`text-xs px-3 py-2 border transition-colors ${
              tiers.published
                ? "border-green-600 text-green-700 bg-green-50 hover:bg-green-100"
                : "border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"
            }`}
          >
            {tiers.published ? "Veröffentlicht — deaktivieren" : "Veröffentlichen"}
          </button>
        )}
      </div>

      {error && (
        <div data-testid="sub-tiers-error" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-xs text-red-700">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div data-testid="sub-tiers-success" className="p-3 bg-green-50 border border-green-200 text-xs text-green-700 flex items-center gap-2">
          <CheckCircle2 size={14} /> {success}
        </div>
      )}

      <div data-testid="sub-tier-row-premium" className="border border-[#E5E5E5] bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-brand-black" />
            <div>
              <p className="text-sm font-semibold text-brand-black">Premium</p>
              <p className="text-[11px] text-[#666]">{t("studio.monthly_auto_renewal")}</p>
            </div>
          </div>
          <div className="relative w-32">
            <input
              data-testid="sub-tier-input-premium"
              type="number"
              step="0.01"
              min={bounds.min_price_eur}
              max={bounds.max_price_eur}
              placeholder="4,99"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full h-10 px-3 pr-8 border border-[#E5E5E5] text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-black"
            />
            <Euro size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999]" />
          </div>
        </div>
      </div>

      {validation.errs.length > 0 && (
        <ul data-testid="sub-tiers-validation-list" className="space-y-1 text-[11px] text-amber-700">
          {validation.errs.map((msg) => (<li key={msg}>• {msg}</li>))}
        </ul>
      )}

      <button
        data-testid="sub-tiers-save"
        onClick={save}
        disabled={saving || !validation.ok}
        className="w-full h-11 bg-brand-black text-brand-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-neutral-800 transition-colors disabled:opacity-40"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? "Speichert…" : "Preis speichern"}
      </button>

      {tiers?.updated_at && (
        <p data-testid="sub-tiers-updated-at" className="text-[10px] text-[#999]">
          Zuletzt geändert: {new Date(tiers.updated_at).toLocaleString("de-DE")}
        </p>
      )}
    </section>
  );
}
